import type { Role, UserListItem } from '@atlas/api-contract';
import { Search, X } from 'lucide-react';
import { useCallback, useState } from 'react';

import { PaginationBar } from '@/components/pagination-bar';
import {
  EmptyState,
  ErrorState,
  SkeletonGrid,
  toDisplayableError,
} from '@/components/states';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCurrentUser } from '@/features/auth';
import { formatAbsoluteTime, formatRelativeTime } from '@/lib/format/date';
import { useDebouncedSearch } from '@/lib/hooks/use-debounced-search';

import { useUpdateUser, useUsersQuery } from './users-queries';
import {
  resolveUsersSearch,
  type UsersSearch,
} from './users-search';

/** The roles a user can be given, in the order they are offered. */
const ROLE_OPTIONS: ReadonlyArray<{ value: Role; label: string }> = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'DESIGNER', label: 'Designer' },
  { value: 'COMMENTER', label: 'Commenter' },
];

const initial = (name: string): string =>
  name.trim().charAt(0).toUpperCase() || '?';

interface UserRowProps {
  readonly user: UserListItem;
  /** The signed-in admin, so their own row can be protected. */
  readonly currentUserId: string;
  /**
   * Whether this row is the last *active* admin on the page. A convenience
   * guard only — the API refuses the last-admin demotion/deactivation for real
   * (spec R7.7), and its 409 is surfaced inline when the guard is wrong across
   * pages.
   */
  readonly isLastActiveAdmin: boolean;
}

/**
 * One user in the roster (spec R7.7).
 *
 * The two irreversible-looking controls — the role select and the active toggle
 * — are disabled for the cases the API forbids (an admin changing their own
 * role, and demoting or deactivating the last active admin), and any refusal
 * the UI did not anticipate is shown inline rather than as a toast that scrolls
 * away from the row it belongs to.
 */
const UserRow = ({ user, currentUserId, isLastActiveAdmin }: UserRowProps) => {
  const updateUser = useUpdateUser();
  const [error, setError] = useState<string | null>(null);

  const isSelf = user.id === currentUserId;
  /* An admin cannot change their own role; nor can the last active admin. */
  const roleLocked = isSelf || (user.role === 'ADMIN' && isLastActiveAdmin);
  /* The last active admin cannot be deactivated. */
  const deactivateLocked =
    user.isActive && user.role === 'ADMIN' && isLastActiveAdmin;

  const runUpdate = useCallback(
    (payload: Parameters<typeof updateUser.mutate>[0]['payload']) => {
      setError(null);
      updateUser.mutate(
        { userId: user.id, payload },
        {
          onError: (caught) => {
            setError(toDisplayableError(caught));
          },
        },
      );
    },
    [updateUser, user.id],
  );

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-2.5">
            <Avatar className="size-7">
              {user.avatarUrl === null ? null : (
                <AvatarImage src={user.avatarUrl} alt="" />
              )}
              <AvatarFallback className="bg-secondary text-[11px] font-semibold text-secondary-foreground">
                {initial(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">
                {user.name}
                {isSelf ? (
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    (you)
                  </span>
                ) : null}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {user.email}
              </p>
            </div>
          </div>
        </TableCell>

        <TableCell>
          <Select
            value={user.role}
            disabled={roleLocked || updateUser.isPending}
            onValueChange={(value) => {
              if (value !== user.role) {
                runUpdate({ role: value as Role });
              }
            }}
          >
            <SelectTrigger
              className="h-8 w-36"
              aria-label={`Role for ${user.name}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>

        <TableCell>
          {user.isActive ? (
            <Badge variant="secondary" className="font-normal">
              Active
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="border-destructive/40 font-normal text-destructive"
            >
              Deactivated
            </Badge>
          )}
        </TableCell>

        <TableCell className="text-xs text-muted-foreground">
          {user.lastSeenAt === null ? (
            'Never'
          ) : (
            <time
              dateTime={user.lastSeenAt}
              title={formatAbsoluteTime(user.lastSeenAt)}
            >
              {formatRelativeTime(user.lastSeenAt)}
            </time>
          )}
        </TableCell>

        <TableCell className="text-right">
          <Button
            type="button"
            variant={user.isActive ? 'outline' : 'secondary'}
            size="sm"
            disabled={deactivateLocked || updateUser.isPending}
            onClick={() => {
              runUpdate({ isActive: !user.isActive });
            }}
          >
            {user.isActive ? 'Deactivate' : 'Reactivate'}
          </Button>
        </TableCell>
      </TableRow>

      {error === null ? null : (
        <TableRow>
          <TableCell colSpan={5} className="pt-0">
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive"
            >
              {error}
            </p>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export interface UsersScreenProps {
  /** The URL's search state — the single source of truth for this view. */
  search: UsersSearch;
  onSearchChange: (next: UsersSearch, replace?: boolean) => void;
}

/**
 * The admin roster (spec R7.7): who exists, their role, and whether they can
 * still sign in — with the controls to change the first two.
 *
 * Admin-only. The route is hidden from everyone else, and the API answers `403`
 * to anyone who reaches the endpoints regardless (spec R7.8).
 */
export const UsersScreen = ({ search, onSearchChange }: UsersScreenProps) => {
  const resolved = resolveUsersSearch(search);
  const query = useUsersQuery(search);
  const { id: currentUserId } = useCurrentUser();

  const commitSearch = useCallback(
    (value: string) => {
      onSearchChange(
        { ...(value === '' ? {} : { search: value }) },
        true,
      );
    },
    [onSearchChange],
  );

  const [draft, setDraft, clearDraft] = useDebouncedSearch(
    resolved.search,
    commitSearch,
  );

  const handlePageChange = useCallback(
    (page: number) => {
      onSearchChange({
        ...(resolved.search === '' ? {} : { search: resolved.search }),
        ...(page === 1 ? {} : { page }),
      });
    },
    [onSearchChange, resolved.search],
  );

  const renderList = () => {
    if (query.isPending) {
      return <SkeletonGrid count={4} label="Loading users" />;
    }

    if (query.isError) {
      return (
        <ErrorState
          title="Could not load users"
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      );
    }

    const { items, total } = query.data;

    if (items.length === 0) {
      return (
        <EmptyState
          icon={Search}
          title={
            resolved.search === ''
              ? 'No users yet'
              : `No users match “${resolved.search}”`
          }
          description="Users appear here the first time they sign in."
          action={
            resolved.search === '' ? undefined : (
              <Button variant="outline" onClick={clearDraft}>
                Clear search
              </Button>
            )
          }
        />
      );
    }

    const activeAdmins = items.filter(
      (user) => user.role === 'ADMIN' && user.isActive,
    ).length;

    return (
      <>
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead className="text-right">Access</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  currentUserId={currentUserId ?? ''}
                  isLastActiveAdmin={
                    user.role === 'ADMIN' && user.isActive && activeAdmins <= 1
                  }
                />
              ))}
            </TableBody>
          </Table>
        </div>

        <PaginationBar
          page={resolved.page}
          pageSize={resolved.pageSize}
          total={total}
          onPageChange={handlePageChange}
          itemNoun="users"
        />
      </>
    );
  };

  return (
    <div className="space-y-6">
      <div className="relative w-full sm:max-w-xs">
        <Search
          aria-hidden="true"
          strokeWidth={1.75}
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          type="search"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          placeholder="Search users"
          aria-label="Search users by name or email"
          aria-busy={query.isFetching}
          className="pl-9 pr-9 [&::-webkit-search-cancel-button]:appearance-none"
        />
        {draft === '' ? null : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={clearDraft}
            className="absolute right-1 top-1/2 size-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X aria-hidden="true" />
            <span className="sr-only">Clear search</span>
          </Button>
        )}
      </div>

      {renderList()}
    </div>
  );
};
